export const PATH_KEYS = ['all', 'group', 'church', 'member'];
export const BRANCH_KEYS = ['group', 'church', 'member'];

export const BRANCH_LABELS = { group: 'Group', church: 'Church', member: 'Member' };

const STEP_LABELS = {
  welcome: 'Welcome',
  hook: 'Hook',
  'value-prayer': 'Value: prayer',
  'value-events': 'Value: events',
  'value-chat': 'Value: chat',
  'value-announce': 'Value: announcements',
  proof: 'Reviews',
  'who-for': 'Who it is for',
  account: 'Create account',
  email: 'Email and name',
  'email-code': 'Email code',
  'email-password': 'Password',
  'welcome-back': 'Welcome back',
  'name-group': 'Name the group',
  'name-church': 'Name the church',
  'church-found': 'Church already on Flock',
  paywall: 'Paywall',
  invite: 'Invite',
  'member-join': 'Join with a code',
  'find-church': 'Find your church',
  'member-done': 'Joined',
  'sign-in': 'Sign in',
};

const STEP_ORDER = Object.keys(STEP_LABELS);

export function stepLabel(step) {
  return STEP_LABELS[step] ?? step;
}

// Each row's `from` is the step whose flows it is measured against. A side path
// (`entry`) is a share of its parent, not a drop. `besides` removes the flows
// that took another branch at the parent, so they don't read as lost.
const EMAIL_SIGN_UP = [
  { step: 'email', from: 'account', entry: true },
  { step: 'email-code', from: 'email' },
  { step: 'email-password', from: 'email-code' },
  { step: 'welcome-back', from: 'account', entry: true },
];

const OPENING = [
  { step: 'welcome', from: null },
  { step: 'hook', from: 'welcome', besides: ['joining'] },
  { step: 'value-prayer', from: 'hook' },
  { step: 'value-events', from: 'value-prayer' },
  { step: 'value-chat', from: 'value-events' },
  { step: 'value-announce', from: 'value-chat' },
  { step: 'proof', from: 'value-announce', optional: true },
  { step: 'who-for', from: 'proof' },
];

const BRANCHES = {
  group: [
    { step: 'account', from: null },
    ...EMAIL_SIGN_UP,
    { step: 'name-group', from: 'account' },
    { step: 'paywall', from: 'name-group' },
    { step: 'invite', from: 'paywall' },
  ],
  church: [
    { step: 'account', from: null },
    ...EMAIL_SIGN_UP,
    { step: 'name-church', from: 'account' },
    { step: 'church-found', from: 'name-church', entry: true },
    { step: 'paywall', from: 'name-church' },
    { step: 'invite', from: 'paywall' },
  ],
  member: [
    { step: 'member-join', from: null },
    { step: 'find-church', from: 'member-join', entry: true },
    { step: 'account', from: 'member-join' },
    ...EMAIL_SIGN_UP,
    { step: 'member-done', from: 'account' },
  ],
};

export const MILESTONES = [
  { key: 'account', label: 'Account' },
  { key: 'consent', label: 'Consent' },
  { key: 'createdOrJoined', label: 'Created or joined' },
  { key: 'paywall', label: 'Paywall' },
  { key: 'subscribed', label: 'Subscribed' },
  { key: 'completed', label: 'Completed' },
];

const CHOICE_LABELS = {
  start: 'Starting Flock',
  joining: 'Joining a church',
  'sign-in': 'Signed in instead',
  group: 'Group',
  church: 'Church',
  member: 'Member',
  found: 'Found their church',
  'not-found': 'Not found',
  code: 'Used a join code',
  create: 'A different church',
  skip: 'Skipped',
};

export function choiceLabel(value) {
  if (CHOICE_LABELS[value]) return CHOICE_LABELS[value];
  const words = String(value).replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function rate(part, whole) {
  return whole > 0 && Number.isFinite(part) ? part / whole : null;
}

export function formatPercent(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const percent = Math.round(Number((value * 100).toFixed(6)));
  if (percent === 0 && value > 0) return '<1%';
  return `${percent}%`;
}

export function formatCount(value) {
  return Number(value ?? 0).toLocaleString('en-US');
}

export function formatDuration(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function completionRate(totals) {
  return rate(totals.completed, totals.started);
}

export function isEmptyReport(report) {
  return report.totals.started === 0 && PATH_KEYS.every((key) => report.paths[key].every((stat) => stat.reached === 0));
}

function emptyStat(step) {
  return { step, reached: 0, continued: 0, back: 0, exitedHere: 0, abandonedHere: 0, stuckNow: 0, medianMs: null, p90Ms: null, errors: [], choices: [] };
}

function chosen(row, values = []) {
  return row.choices.filter((choice) => values.includes(choice.value)).reduce((sum, choice) => sum + choice.count, 0);
}

function funnelTable(stats, specs) {
  const byStep = new Map(stats.map((stat) => [stat.step, stat]));
  const unknown = stats.filter((stat) => !STEP_LABELS[stat.step]).map((stat) => ({ step: stat.step, from: null }));
  const rows = new Map();
  const parentOf = new Map();

  for (const spec of [...specs, ...unknown]) {
    const stat = byStep.get(spec.step) ?? emptyStat(spec.step);
    const parent = spec.from ? rows.get(spec.from) : null;
    let base = parent;
    while (base && base.reached === 0) base = parentOf.get(base.step);

    const baseCount = base ? base.reached - (base === parent ? chosen(base, spec.besides) : 0) : null;
    const measured = base && baseCount > 0 && !(spec.optional && stat.reached === 0);
    const dropCount = measured && !spec.entry ? Math.max(baseCount - stat.reached, 0) : null;

    parentOf.set(spec.step, parent);
    rows.set(spec.step, {
      ...stat,
      label: stepLabel(spec.step),
      entry: Boolean(spec.entry),
      side: Boolean(spec.entry || parent?.side),
      continuedRate: rate(stat.continued, stat.reached),
      backRate: rate(stat.back, stat.reached),
      base: base ? { step: base.step, label: base.label, count: baseCount } : null,
      dropCount,
      dropRate: dropCount === null ? null : rate(dropCount, baseCount),
      shareOfBase: measured && spec.entry ? rate(stat.reached, baseCount) : null,
    });
  }

  return [...rows.values()];
}

export function openingRows(report) {
  return funnelTable(report.paths.all, OPENING);
}

export function branchRows(report, path) {
  return funnelTable(report.paths[path], BRANCHES[path]);
}

export function barSegments(row, top) {
  if (!top) return { kept: 0, dropped: 0 };
  const kept = Math.min((row.reached / top) * 100, 100);
  const dropped = row.entry || !row.dropCount ? 0 : Math.min((row.dropCount / top) * 100, 100 - kept);
  return { kept, dropped };
}

function flowOrder(step) {
  const index = STEP_ORDER.indexOf(step);
  return index === -1 ? STEP_ORDER.length : index;
}

// The split is over the flows that reached the step, so a step some flows
// leave without a choice (Back, or the edge swipe) still adds up.
export function choiceSplits(report) {
  return report.paths.all
    .filter((stat) => stat.choices.length > 0)
    .sort((a, b) => flowOrder(a.step) - flowOrder(b.step))
    .map((stat) => {
      const chose = stat.choices.reduce((sum, choice) => sum + choice.count, 0);
      const options = [...stat.choices].sort((a, b) => b.count - a.count);
      if (stat.reached > chose) options.push({ value: null, count: stat.reached - chose });
      return {
        step: stat.step,
        label: stepLabel(stat.step),
        total: stat.reached,
        options: options.map((choice) => ({
          ...choice,
          label: choice.value === null ? 'No choice recorded' : choiceLabel(choice.value),
          rate: rate(choice.count, stat.reached),
        })),
      };
    });
}

export function cohortRows(report) {
  return [...report.cohorts]
    .sort((a, b) => b.day.localeCompare(a.day))
    .map((cohort) => ({
      day: cohort.day,
      started: cohort.started,
      milestones: MILESTONES.map(({ key, label }) => ({
        key,
        label,
        count: cohort.reached[key],
        rate: rate(cohort.reached[key], cohort.started),
      })),
    }));
}

const CLIENT_EVENTS = {
  flow_started: 'Started onboarding',
  flow_resumed: 'Reopened onboarding',
  step_viewed: 'Saw the step',
  step_left: 'Left the step',
  choice: 'Chose',
  error: 'Saw an error',
  app_backgrounded: 'Left the app',
  app_foregrounded: 'Came back to the app',
  flow_completed: 'Finished onboarding',
  flow_exited: 'Left onboarding',
};

const SERVER_EVENTS = {
  user_signup: 'Signed up',
  AI_CONSENT_ACCEPTED: 'Accepted AI consent',
  AI_CONSENT_DECLINED: 'Declined AI consent',
  join_code_attempted: 'Tried a join code',
  personal_group_created: 'Created a personal group',
  join_request_submitted: 'Asked to join a church',
  org_creation_started: 'Started creating a church',
  onboarding_skipped: 'Skipped onboarding',
  group_created: 'Created a group',
  join_code_generated: 'Made a join code',
  apple_subscription_verified: 'Apple subscription verified',
  group_joined_with_code: 'Joined a group with a code',
  billing_paywall_viewed: 'Saw the paywall',
};

export function eventLabel(event) {
  if (event.source === 'server') {
    return SERVER_EVENTS[event.event] ?? (event.category ? `${event.category} / ${event.event}` : event.event);
  }
  return CLIENT_EVENTS[event.event] ?? event.event;
}

function placeLabel(target) {
  if (target === 'home') return 'Home';
  if (target === 'exit') return 'out of onboarding';
  return stepLabel(target);
}

export function eventDetail(event) {
  return [
    event.to ? `to ${placeLabel(event.to)}` : null,
    event.via ? `by ${event.via}` : null,
    event.ms !== null && event.ms !== undefined ? `after ${formatDuration(event.ms)}` : null,
    event.choice ? choiceLabel(event.choice) : null,
    event.code ? `code ${event.code}` : null,
    ...(event.details ?? []).map(([key, value]) => `${key} ${value}`),
  ]
    .filter(Boolean)
    .join(' · ');
}

const CENTRAL_TIME = 'America/Chicago';

export function formatWhen(at) {
  const date = new Date(at);
  if (!at || Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleString('en-US', {
    timeZone: CENTRAL_TIME,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} CT`;
}

export function formatDay(day) {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
}

const VARIANTS = ['production', 'staging', 'dev'];
const AUDIENCES = ['all', ...BRANCH_KEYS];
const PLATFORMS = ['all', 'ios', 'android', 'web'];

export const VARIANT_OPTIONS = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
  { value: 'dev', label: 'Development' },
];
export const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All paths' },
  ...BRANCH_KEYS.map((key) => ({ value: key, label: BRANCH_LABELS[key] })),
];
export const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All platforms' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
];

function isDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value ?? '')) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function shiftDay(day, days) {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function localDay(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function defaultFilters(today) {
  return { from: shiftDay(today, -13), to: today, variant: 'production', audience: 'all', appVersion: '', platform: 'all' };
}

export function readFilters(params, today) {
  const defaults = defaultFilters(today);
  const pick = (key, allowed) => (allowed.includes(params.get(key)) ? params.get(key) : defaults[key]);
  const day = (key) => (isDay(params.get(key)) ? params.get(key) : defaults[key]);
  return {
    from: day('from'),
    to: day('to'),
    variant: pick('variant', VARIANTS),
    audience: pick('audience', AUDIENCES),
    appVersion: /^\d+(\.\d+){0,3}$/.test(params.get('version') ?? '') ? params.get('version') : defaults.appVersion,
    platform: pick('platform', PLATFORMS),
  };
}

const URL_KEYS = { from: 'from', to: 'to', variant: 'variant', audience: 'audience', appVersion: 'version', platform: 'platform' };

export function flowHref(pathname, search, extra = {}) {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function filterSearch(filters, today) {
  const defaults = defaultFilters(today);
  const params = new URLSearchParams();
  for (const [key, urlKey] of Object.entries(URL_KEYS)) {
    if (filters[key] !== defaults[key]) params.set(urlKey, filters[key]);
  }
  return params.toString();
}
